import { useRef } from "react";
import { fileUrl } from "@/lib/api";
import { Star, Trash2, RefreshCw, ImagePlus } from "lucide-react";

const uid = () =>
  (window.crypto?.randomUUID && window.crypto.randomUUID()) || `k-${Math.random().toString(36).slice(2)}`;

export function itemsFromPaths(paths = []) {
  return paths.map((p) => ({ key: uid(), path: p, preview: fileUrl(p) }));
}

export default function PhotoManager({ items, onChange }) {
  const addRef = useRef(null);
  const replaceRefs = useRef({});

  const addFiles = (files) => {
    const additions = Array.from(files).map((f) => ({ key: uid(), file: f, preview: URL.createObjectURL(f) }));
    onChange([...items, ...additions]);
  };

  const removeItem = (key) => onChange(items.filter((i) => i.key !== key));

  const setMain = (key) => {
    const idx = items.findIndex((i) => i.key === key);
    if (idx <= 0) return;
    const copy = [...items];
    const [it] = copy.splice(idx, 1);
    copy.unshift(it);
    onChange(copy);
  };

  const replaceItem = (key, file) =>
    onChange(items.map((i) => (i.key === key ? { ...i, file, path: undefined, preview: URL.createObjectURL(file) } : i)));

  return (
    <div data-testid="photo-manager">
      <input
        ref={addRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        data-testid="photo-add-input"
        onChange={(e) => {
          if (e.target.files?.length) addFiles(e.target.files);
          e.target.value = "";
        }}
      />

      {items.length === 0 ? (
        <button
          type="button"
          data-testid="photo-add-btn"
          onClick={() => addRef.current?.click()}
          className="flex h-40 w-full flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-slate-200 text-slate-400 transition-colors hover:border-[#C6A87C] hover:text-[#C6A87C]"
        >
          <ImagePlus className="h-7 w-7" />
          <span className="text-sm font-medium">Téléverser des photos</span>
          <span className="text-xs">1 image principale + au moins 2 secondaires</span>
        </button>
      ) : (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          {items.map((it, idx) => (
            <div
              key={it.key}
              data-testid={`photo-item-${idx}`}
              className={`group relative aspect-[4/3] overflow-hidden rounded-xl border ${
                idx === 0 ? "border-[#C6A87C] ring-2 ring-[#C6A87C]/30" : "border-slate-200"
              }`}
            >
              <img src={it.preview} alt="" className="h-full w-full object-cover" />
              {idx === 0 && (
                <span className="absolute left-2 top-2 rounded-full bg-[#C6A87C] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-[#0F172A]" data-testid={`photo-main-badge-${idx}`}>
                  Principale
                </span>
              )}
              <div className="absolute inset-0 flex items-center justify-center gap-1.5 bg-black/45 opacity-0 transition-opacity group-hover:opacity-100">
                {idx !== 0 && (
                  <button
                    type="button"
                    title="Définir comme principale"
                    data-testid={`photo-set-main-${idx}`}
                    onClick={() => setMain(it.key)}
                    className="flex h-8 w-8 items-center justify-center rounded-full bg-white/90 text-[#0F172A] hover:bg-white"
                  >
                    <Star className="h-4 w-4" />
                  </button>
                )}
                <button
                  type="button"
                  title="Remplacer"
                  data-testid={`photo-replace-${idx}`}
                  onClick={() => replaceRefs.current[it.key]?.click()}
                  className="flex h-8 w-8 items-center justify-center rounded-full bg-white/90 text-[#0F172A] hover:bg-white"
                >
                  <RefreshCw className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  title="Supprimer"
                  data-testid={`photo-delete-${idx}`}
                  onClick={() => removeItem(it.key)}
                  className="flex h-8 w-8 items-center justify-center rounded-full bg-white/90 text-red-600 hover:bg-white"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
              <input
                ref={(el) => (replaceRefs.current[it.key] = el)}
                type="file"
                accept="image/*"
                className="hidden"
                data-testid={`photo-replace-input-${idx}`}
                onChange={(e) => {
                  if (e.target.files?.[0]) replaceItem(it.key, e.target.files[0]);
                  e.target.value = "";
                }}
              />
            </div>
          ))}

          <button
            type="button"
            data-testid="photo-add-btn"
            onClick={() => addRef.current?.click()}
            className="flex aspect-[4/3] flex-col items-center justify-center gap-1.5 rounded-xl border-2 border-dashed border-slate-200 text-slate-400 transition-colors hover:border-[#C6A87C] hover:text-[#C6A87C]"
          >
            <ImagePlus className="h-6 w-6" />
            <span className="text-xs font-medium">Ajouter</span>
          </button>
        </div>
      )}
    </div>
  );
}
